// Auth store — manages authentication + MFA state (Zustand)

import { create } from 'zustand';
import type { AuthUser } from '@soeinscheiss/shared';
import * as authApi from '../features/auth/api.js';
import i18n from '../lib/i18n.js';

/** MFA challenge state when login requires 2FA */
export interface MfaChallenge {
  mfaChallengeToken: string;
  mfaMethods: ('totp' | 'passkey')[];
}

export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaChallenge: MfaChallenge | null;

  /** Set user after successful login */
  setUser: (user: AuthUser | null) => void;

  /** Set MFA challenge (login returned mfaRequired) */
  setMfaChallenge: (challenge: MfaChallenge | null) => void;

  /** Clear MFA challenge state */
  clearMfaChallenge: () => void;

  /** Full logout — calls API + clears state */
  logout: () => Promise<void>;

  /** Restore session on page load — tries token refresh */
  restoreSession: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  mfaChallenge: null,

  setUser: (user) => {
    // Sync i18n language to user's saved locale
    if (user?.locale && user.locale !== i18n.language) {
      i18n.changeLanguage(user.locale);
      localStorage.setItem('locale', user.locale);
    }
    set({ user, isAuthenticated: !!user, isLoading: false });
  },

  setMfaChallenge: (challenge) => set({ mfaChallenge: challenge }),

  clearMfaChallenge: () => set({ mfaChallenge: null }),

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout API failures — clear state regardless
    }
    localStorage.removeItem('accessToken');
    set({ user: null, isAuthenticated: false, mfaChallenge: null });
  },

  restoreSession: async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      set({ isLoading: false });
      return;
    }

    try {
      // Validate current token by fetching user profile
      const res = await authApi.getMe();
      set({ user: res.data.user, isAuthenticated: true, isLoading: false });
    } catch {
      // Token invalid — try refresh
      try {
        const refreshRes = await authApi.refreshToken();
        localStorage.setItem('accessToken', refreshRes.data.accessToken);
        set({ user: refreshRes.data.user, isAuthenticated: true, isLoading: false });
      } catch {
        // Refresh failed — no valid session
        localStorage.removeItem('accessToken');
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    }
  },
}));
